/* LA FRU TEA — product gallery + lightbox (lft1)
   File: assets/lft-product-gallery.js
   Elements: <lft-gallery>, <lft-lightbox>
   No dependencies. Optional: Dawn pubsub (variant change → featured image). */

(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Shared: scroll-snap track helper */
  const trackIndex = (track) => {
    const w = track.clientWidth || 1;
    return Math.round(track.scrollLeft / w);
  };
  const scrollTrackTo = (track, i, smooth = true) => {
    track.scrollTo({ left: i * track.clientWidth, behavior: smooth && !reduceMotion ? 'smooth' : 'auto' });
  };
  const keepThumbVisible = (list, el) => {
    if (!list || !el) return;
    const l = list.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    if (r.left < l.left || r.right > l.right) {
      list.scrollTo({ left: el.offsetLeft - (list.clientWidth - el.clientWidth) / 2, behavior: reduceMotion ? 'auto' : 'smooth' });
    }
  };

  /* ================= Gallery ================= */
  if (!customElements.get('lft-gallery')) {
    customElements.define(
      'lft-gallery',
      class LftGallery extends HTMLElement {
        connectedCallback() {
          this.track = this.querySelector('[data-track]');
          this.slides = Array.from(this.track.children);
          this.thumbs = Array.from(this.querySelectorAll('[data-thumbs] [data-go]'));
          this.thumbList = this.querySelector('[data-thumbs]');
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.current = this.querySelector('[data-current]');
          this.index = 0;

          this.prev?.addEventListener('click', () => this.go(this.index - 1));
          this.next?.addEventListener('click', () => this.go(this.index + 1));
          this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(+t.dataset.go)));

          let raf;
          this.track.addEventListener('scroll', () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => this.sync(trackIndex(this.track)));
          }, { passive: true });

          this.track.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
          });

          this.addEventListener('click', (e) => {
            const opener = e.target.closest('[data-open-lightbox]');
            if (!opener) return;
            const lb = document.getElementById(`LftLightbox-${this.dataset.section}`);
            lb?.open(opener.dataset.openLightbox, opener);
          });

          window.addEventListener('resize', () => scrollTrackTo(this.track, this.index, false));

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsub = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (!data || String(data.sectionId) !== String(this.dataset.section)) return;
              const id = data.variant?.featured_media?.id;
              if (id) this.goToMedia(id);
            });
          }
          this.sync(0);
        }

        disconnectedCallback() { this.unsub?.(); }

        go(i) {
          const max = this.slides.length - 1;
          i = Math.max(0, Math.min(max, i));
          scrollTrackTo(this.track, i);
          this.sync(i);
        }

        goToMedia(mediaId) {
          const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
          if (i > -1) this.go(i);
        }

        sync(i) {
          if (i === this.index && this._synced) return;
          this._synced = true;
          if (this.slides[this.index] !== this.slides[i]) window.pauseAllMedia?.();
          this.index = i;
          this.slides.forEach((s, n) => s.classList.toggle('is-active', n === i));
          this.thumbs.forEach((t, n) => {
            t.classList.toggle('is-active', n === i);
            n === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current');
          });
          keepThumbVisible(this.thumbList, this.thumbs[i]?.parentElement);
          if (this.current) this.current.textContent = i + 1;
          if (this.prev) this.prev.disabled = i === 0;
          if (this.next) this.next.disabled = i === this.slides.length - 1;
        }
      }
    );
  }

  /* ================= Lightbox ================= */
  if (!customElements.get('lft-lightbox')) {
    customElements.define(
      'lft-lightbox',
      class LftLightbox extends HTMLElement {
        connectedCallback() {
          this.dialog = this.querySelector('dialog');
          // Move to <body> so no ancestor transform/overflow can clip it
          if (this.parentElement !== document.body) {
            // Theme editor re-renders: drop a stale copy already living in <body>
            document.querySelectorAll(`lft-lightbox[id="${this.id}"]`).forEach((el) => el !== this && el.remove());
            document.body.appendChild(this);
            return; // connectedCallback fires again after move
          }
          if (this._ready) return;
          this._ready = true;

          this.track = this.querySelector('[data-track]');
          this.slides = Array.from(this.track.children);
          this.thumbs = Array.from(this.querySelectorAll('[data-thumbs] [data-go]'));
          this.thumbList = this.querySelector('[data-thumbs]');
          this.prev = this.querySelector('[data-prev]');
          this.next = this.querySelector('[data-next]');
          this.current = this.querySelector('[data-current]');
          this.index = 0;

          this.querySelector('[data-close]').addEventListener('click', () => this.close());
          this.prev?.addEventListener('click', () => this.go(this.index - 1));
          this.next?.addEventListener('click', () => this.go(this.index + 1));
          this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(+t.dataset.go)));

          let raf;
          this.track.addEventListener('scroll', () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
              const i = trackIndex(this.track);
              if (i !== this.index) { this.resetZoom(); this.sync(i); }
            });
          }, { passive: true });

          this.dialog.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
            if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
          });
          this.dialog.addEventListener('close', () => this.onClosed());
          // Click on empty area around image closes (not on image, arrows, thumbs)
          this.dialog.addEventListener('click', (e) => {
            if (e.target.classList.contains('lft-lb__slide')) this.close();
          });

          this.slides.forEach((slide) => this.bindZoom(slide.querySelector('[data-zoom]')));
        }

        open(mediaId, opener) {
          this.opener = opener;
          let i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
          if (i < 0) i = 0;
          this.load(i); this.load(i - 1); this.load(i + 1);
          this.dialog.showModal();
          document.documentElement.style.overflow = 'hidden';
          requestAnimationFrame(() => {
            scrollTrackTo(this.track, i, false);
            this.sync(i, true);
          });
        }

        close() { this.dialog.close(); }

        onClosed() {
          this.resetZoom();
          document.documentElement.style.overflow = '';
          // Return main gallery to the image the user ended on
          const id = this.slides[this.index]?.dataset.mediaId;
          const gallery = document.getElementById(`LftGallery-${this.dataset.section}`);
          if (id && gallery?.goToMedia) gallery.goToMedia(id);
          this.opener?.focus({ preventScroll: true });
        }

        go(i) {
          const max = this.slides.length - 1;
          i = Math.max(0, Math.min(max, i));
          this.resetZoom();
          scrollTrackTo(this.track, i);
          this.sync(i);
        }

        sync(i, force) {
          if (i === this.index && !force) return;
          this.index = i;
          this.load(i); this.load(i - 1); this.load(i + 1);
          this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
          keepThumbVisible(this.thumbList, this.thumbs[i]?.parentElement);
          if (this.current) this.current.textContent = i + 1;
          if (this.prev) this.prev.disabled = i === 0;
          if (this.next) this.next.disabled = i === this.slides.length - 1;
        }

        load(i) {
          const img = this.slides[i]?.querySelector('img[data-src]');
          if (img && !img.src) img.src = img.dataset.src;
        }

        /* ---- Zoom: click (desktop) / double-tap (touch) toggles 2.5x, drag/mouse pans ---- */
        bindZoom(box) {
          if (!box) return;
          const img = box.querySelector('img');
          const SCALE = 2.5;
          let zoomed = false, x = 0, y = 0, startX = 0, startY = 0, dragging = false, moved = false, lastTap = 0;

          const clamp = () => {
            const r = img.getBoundingClientRect();
            const baseW = r.width / (zoomed ? SCALE : 1);
            const baseH = r.height / (zoomed ? SCALE : 1);
            const maxX = Math.max(0, (baseW * SCALE - box.clientWidth) / 2);
            const maxY = Math.max(0, (baseH * SCALE - box.clientHeight) / 2);
            x = Math.max(-maxX, Math.min(maxX, x));
            y = Math.max(-maxY, Math.min(maxY, y));
          };
          const apply = () => { img.style.transform = zoomed ? `translate(${x}px, ${y}px) scale(${SCALE})` : ''; };

          const zoomAt = (cx, cy) => {
            const r = img.getBoundingClientRect();
            zoomed = true;
            // keep the clicked point under the cursor
            x = (r.left + r.width / 2 - cx) * (SCALE - 1);
            y = (r.top + r.height / 2 - cy) * (SCALE - 1);
            box.classList.add('is-zoomed');
            this.dialog.classList.add('is-zoomed');
            clamp(); apply();
          };
          const unzoom = () => {
            zoomed = false; x = 0; y = 0;
            box.classList.remove('is-zoomed', 'is-dragging');
            this.dialog.classList.remove('is-zoomed');
            apply();
          };
          box._unzoom = unzoom;

          box.addEventListener('pointerdown', (e) => {
            moved = false;
            if (!zoomed) return;
            dragging = true; startX = e.clientX - x; startY = e.clientY - y;
            box.setPointerCapture(e.pointerId);
            box.classList.add('is-dragging');
          });
          box.addEventListener('pointermove', (e) => {
            if (!dragging) return;
            const nx = e.clientX - startX, ny = e.clientY - startY;
            if (Math.abs(nx - x) + Math.abs(ny - y) > 2) moved = true;
            x = nx; y = ny; clamp(); apply();
          });
          const end = () => { dragging = false; box.classList.remove('is-dragging'); };
          box.addEventListener('pointerup', end);
          box.addEventListener('pointercancel', end);

          box.addEventListener('click', (e) => {
            if (moved) { moved = false; return; }
            const touch = e.pointerType === 'touch' || (e.sourceCapabilities && e.sourceCapabilities.firesTouchEvents);
            if (touch) {
              const now = Date.now();
              if (now - lastTap < 300) { zoomed ? unzoom() : zoomAt(e.clientX, e.clientY); lastTap = 0; }
              else lastTap = now;
              return;
            }
            zoomed ? unzoom() : zoomAt(e.clientX, e.clientY);
          });
        }

        resetZoom() { this.slides?.forEach((s) => s.querySelector('[data-zoom]')?._unzoom?.()); }
      }
    );
  }
})();

/* ---- LFT variant sync: elements with [data-lft-sync] and an id re-render from Dawn's variant HTML ---- */
(() => {
  const init = () => {
    if (window.__lftSync || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    window.__lftSync = true;
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.html) return;
      data.html.querySelectorAll('[data-lft-sync][id]').forEach((src) => {
        const dest = document.getElementById(src.id);
        if (!dest) return;
        dest.innerHTML = src.innerHTML;
        dest.hidden = src.hidden;
      });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
